import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon, Cog6ToothIcon, LockClosedIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useAuth } from '../../providers/auth/useAuth'
import AddBrandDialog from '../brand/AddBrandDialog'

const ITEM =
  'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[13px] text-stone-700 transition-colors data-focus:bg-stone-100 data-focus:text-stone-950 md:py-2'

function BrandMark({ name }: { name: string }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center">
      <span className="flex size-6 items-center justify-center rounded-md bg-stone-200 text-xs font-medium text-stone-700">
        {name.charAt(0).toUpperCase()}
      </span>
    </span>
  )
}

export default function BrandSwitcher({ expanded }: { expanded: boolean }) {
  const { account, brand, selectBrand } = useAuth()
  const navigate = useNavigate()
  const [adding, setAdding] = useState(false)
  const brands = account?.brands ?? []
  const name = brand?.name ?? 'Adlovin'

  return (
    <>
      <Menu>
        <MenuButton
          aria-label="Switch brand"
          className="flex h-10 min-w-0 flex-1 items-center overflow-hidden rounded-xl text-left whitespace-nowrap transition-colors hover:bg-black/5 md:h-8"
        >
          <BrandMark name={name} />
          {expanded && (
            <>
              <span className="min-w-0 flex-1 truncate text-sm text-stone-950">{name}</span>
              <ChevronUpDownIcon className="mr-1.5 size-4 shrink-0 text-stone-400" />
            </>
          )}
        </MenuButton>

        <MenuItems
          anchor={{ to: 'bottom start', gap: 6 }}
          transition
          className="z-50 max-h-(--anchor-max-height) w-60 overflow-y-auto rounded-xl border border-stone-200 bg-white shadow-[0_2px_4px_rgba(28,25,23,0.04),0_18px_44px_-16px_rgba(28,25,23,0.22)] transition duration-100 ease-out [--anchor-max-height:24rem] data-closed:translate-y-1 data-closed:opacity-0"
        >
          <div className="p-1">
            <p className="px-3 pt-2 pb-1 text-[10px] font-medium tracking-[0.08em] text-stone-400 uppercase">
              Brands · {brands.length} of {account?.brand_limit ?? 1}
            </p>
            {brands.map((item) => (
              <MenuItem key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (item.locked) {
                      navigate('/plans')
                      return
                    }
                    selectBrand(item.id)
                    navigate('/')
                  }}
                  className={ITEM}
                >
                  <span className="min-w-0 flex-1 truncate">{item.name}</span>
                  {item.locked ? (
                    <LockClosedIcon className="size-4 shrink-0 text-stone-400" data-tooltip="Upgrade to unlock" />
                  ) : (
                    item.id === brand?.id && <CheckIcon className="size-4 shrink-0 text-purple-700" />
                  )}
                </button>
              </MenuItem>
            ))}
          </div>

          <div className="border-t border-stone-100 p-1">
            <MenuItem>
              <button type="button" onClick={() => setAdding(true)} className={ITEM}>
                <PlusIcon className="size-4 text-stone-400" />
                Add brand
              </button>
            </MenuItem>
            <MenuItem>
              <Link to="/billing" className={ITEM}>
                <Cog6ToothIcon className="size-4 text-stone-400" />
                Manage brands
              </Link>
            </MenuItem>
          </div>
        </MenuItems>
      </Menu>

      {adding && <AddBrandDialog onClose={() => setAdding(false)} />}
    </>
  )
}
